import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Server, Globe, Megaphone, Shield, Clock, Headphones, 
  CheckCircle, Star, ArrowRight, ChevronLeft, ChevronRight,
  Zap, Lock, BarChart3
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const EUR_RATE = 55.85;

const DURATION_MAP = {
  "شهري": "Monthly", "سنة": "1 Year", "سنتين": "2 Years",
  "3 سنوات": "3 Years", "4 سنوات": "4 Years", "5 سنوات": "5 Years"
};

const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      title: "Professional Web Hosting",
      subtitle: "Ultra-fast Speed & Complete Security",
      description: "High-performance servers with 24/7 technical support",
      image: "https://images.unsplash.com/photo-1645477704075-cb3d14b349ee?w=1200&q=80",
      cta: "Get Started",
      link: "#plans"
    },
    {
      title: "Premium Web Design",
      subtitle: "Modern Designs That Reflect Your Brand",
      description: "We build professional websites that attract your customers",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&q=80",
      cta: "Learn More",
      link: "#plans"
    },
    {
      title: "Digital Marketing",
      subtitle: "Wider Reach & Real Results",
      description: "We help you reach your target audience effectively",
      image: "https://images.unsplash.com/photo-1533750349088-cd871a92f312?w=1200&q=80",
      cta: "Contact Us",
      link: "/contact"
    }
  ];

  const features = [
    { icon: Zap, title: "Ultra-fast Speed", description: "High-performance SSD servers" },
    { icon: Shield, title: "Advanced Security", description: "Free SSL & DDoS protection" },
    { icon: Clock, title: "99.9% Uptime", description: "Guaranteed service availability" },
    { icon: Headphones, title: "24/7 Support", description: "Always available support team" },
    { icon: Lock, title: "Daily Backups", description: "Automatic daily backups" },
    { icon: BarChart3, title: "Control Panel", description: "Easy & flexible management" }
  ];

  const stats = [
    { value: "10,000+", label: "Happy Clients" },
    { value: "99.9%", label: "Uptime" },
    { value: "24/7", label: "Support" },
    { value: "5+", label: "Years Experience" }
  ];

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/api/products`);
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  // Show only monthly plans on homepage
  const monthlyProducts = products;
  const toEur = (egp) => Math.ceil(egp / EUR_RATE);

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950" dir="ltr">
      <Navbar />

      {/* Hero Slider */}
      <section className="relative h-screen overflow-hidden">
        {slides.map((slide, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0 }}
            animate={{ opacity: currentSlide === index ? 1 : 0 }}
            transition={{ duration: 0.5 }}
            className={`absolute inset-0 ${currentSlide === index ? 'z-10' : 'z-0'}`}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-neutral-950/90 to-neutral-900/70 z-10" />
            <img src={slide.image} alt={slide.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 z-20 flex items-center">
              <div className="container mx-auto px-4">
                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="max-w-3xl"
                >
                  <Badge className="mb-4 bg-red-500/20 text-red-300 border-red-500/30">
                    {slide.subtitle}
                  </Badge>
                  <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
                    {slide.title}
                  </h1>
                  <p className="text-xl text-gray-200 mb-8">
                    {slide.description}
                  </p>
                  <a href={slide.link}>
                    <Button size="lg" className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-black font-semibold">
                      {slide.cta}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </a>
                </motion.div>
              </div>
            </div>
          </motion.div>
        ))}

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-30 flex gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                currentSlide === index ? 'bg-red-500 w-8' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
        <button onClick={prevSlide} className="absolute left-4 top-1/2 transform -translate-y-1/2 z-30 bg-white/10 hover:bg-white/20 p-2 rounded-full backdrop-blur-sm">
          <ChevronLeft className="h-6 w-6 text-white" />
        </button>
        <button onClick={nextSlide} className="absolute right-4 top-1/2 transform -translate-y-1/2 z-30 bg-white/10 hover:bg-white/20 p-2 rounded-full backdrop-blur-sm">
          <ChevronRight className="h-6 w-6 text-white" />
        </button>
      </section>

      {/* Features */}
      <section className="py-20 bg-neutral-900/50">
        <div className="container mx-auto px-4">
          <motion.div initial={{ y: 20, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Why Choose Power?</h2>
            <p className="text-xl text-gray-300">We offer the best solutions for your digital success</p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div key={index} initial={{ y: 20, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                <Card className="bg-white/5 border-white/10 hover:bg-white/10 transition-all">
                  <CardHeader>
                    <div className="p-3 bg-red-500/20 rounded-lg w-fit mb-4">
                      <feature.icon className="h-6 w-6 text-red-400" />
                    </div>
                    <CardTitle className="text-white">{feature.title}</CardTitle>
                    <CardDescription className="text-gray-300">{feature.description}</CardDescription>
                  </CardHeader>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section id="plans" className="py-20">
        <div className="container mx-auto px-4">
          <motion.div initial={{ y: 20, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Plans</h2>
            <p className="text-xl text-gray-300 mb-8">Choose the plan that fits your needs</p>
            <Link to="/plans">
              <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                View All Plans & Durations
              </Button>
            </Link>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {monthlyProducts.slice(0, 6).map((product, index) => (
              <motion.div key={product.id} initial={{ y: 20, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                <Card className="bg-white/5 border-white/10 hover:border-red-500/50 transition-all h-full flex flex-col">
                  <CardHeader>
                    <CardTitle className="text-2xl text-white">{product.name_en || product.name_ar}</CardTitle>
                    <CardDescription className="text-gray-300">{product.description_en || product.description_ar}</CardDescription>
                    <div className="mt-4">
                      <span className="text-4xl font-bold text-white">€{product.price_eur || toEur(product.price_total)}</span>
                      <span className="text-gray-400"> /month</span>
                      <div className="text-sm text-gray-500 mt-1">{product.price_total?.toLocaleString()} EGP</div>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1">
                    <ul className="space-y-3">
                      {product.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-300">
                          <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                  <CardFooter>
                    <Link to={`/checkout?product=${product.id}&cycle=monthly`} className="w-full">
                      <Button className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-black font-semibold">
                        Subscribe Now
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-neutral-900/50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div key={index} initial={{ scale: 0.8, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-red-400 mb-2">{stat.value}</div>
                <div className="text-gray-300">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
